/**
 * Utility to aggregate all error configurations into a flat list of entries
 */
import { FlightError } from "@b2bportal/air-booking-api";
import { LodgingErrorEnum } from "@b2bportal/lodging-api";
import { PurchaseErrorEnum } from "@b2bportal/purchase-api";

import { SeatSelectionErrorModal } from "../../types/common";
import {
  FLIGHT_ERROR_CONFIG,
  PURCHASE_ERROR_CONFIG,
  SEAT_SELECTION_ERROR_CONFIG,
} from "../flightErrors/config";
import {
  LODGING_ERROR_CONFIG,
  LODGING_PURCHASE_ERROR_CONFIG,
} from "../lodgingErrors/config";
import type { ErrorEntry } from "./types";

/**
 * Aggregates all error configurations from flights and lodging into a flat list.
 * This is useful for the error config viewer to display all configurations at once.
 */
export function getAllErrorEntries(): ErrorEntry[] {
  const entries: ErrorEntry[] = [];

  // Flight errors - CART_QUOTE
  const flightQuoteConfig = FLIGHT_ERROR_CONFIG.CART_QUOTE;
  for (const errorCode of Object.values(FlightError)) {
    const config = flightQuoteConfig[errorCode] || flightQuoteConfig.default;
    entries.push({
      category: "flights-quote",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Flight errors - CART_FULFILL
  const flightFulfillConfig = FLIGHT_ERROR_CONFIG.CART_FULFILL;
  for (const errorCode of Object.values(FlightError)) {
    const config =
      flightFulfillConfig[errorCode] || flightFulfillConfig.default;
    entries.push({
      category: "flights-fulfill",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Flight purchase errors
  for (const errorCode of Object.values(PurchaseErrorEnum)) {
    const config =
      PURCHASE_ERROR_CONFIG.CART_QUOTE[errorCode] ||
      FLIGHT_ERROR_CONFIG.CART_QUOTE.default;
    entries.push({
      category: "flights-purchase",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Seat selection errors
  for (const errorCode of Object.values(SeatSelectionErrorModal)) {
    const config =
      SEAT_SELECTION_ERROR_CONFIG[errorCode] ||
      SEAT_SELECTION_ERROR_CONFIG.default;
    entries.push({
      category: "flights-seats",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Lodging errors - CART_QUOTE
  const lodgingQuoteConfig = LODGING_ERROR_CONFIG.CART_QUOTE;
  for (const errorCode of Object.values(LodgingErrorEnum)) {
    const config = lodgingQuoteConfig[errorCode] || lodgingQuoteConfig.default;
    entries.push({
      category: "lodging-quote",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Lodging errors - CART_FULFILL
  const lodgingFulfillConfig = LODGING_ERROR_CONFIG.CART_FULFILL;
  for (const errorCode of Object.values(LodgingErrorEnum)) {
    const config =
      lodgingFulfillConfig[errorCode] || lodgingFulfillConfig.default;
    entries.push({
      category: "lodging-fulfill",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  // Lodging purchase errors
  for (const errorCode of Object.values(PurchaseErrorEnum)) {
    const config =
      LODGING_PURCHASE_ERROR_CONFIG.CART_QUOTE[errorCode] ||
      LODGING_ERROR_CONFIG.CART_QUOTE.default;
    entries.push({
      category: "lodging-purchase",
      errorCode,
      titleKey: config.titleKey,
      subtitleKey: config.subtitleKey,
      primaryButtonKey: config.primaryButton.textKey,
      primaryAction: config.primaryButton.action,
      secondaryButtonKey: config.secondaryButton?.textKey,
      secondaryAction: config.secondaryButton?.action,
      className: config.className,
    });
  }

  return entries;
}
